import type { TripRoute } from "@/types/trip";
import { type MaybeRefOrGetter, toValue } from "vue";
import type { DisabledRule } from "@/types/validation";
import { OrderFreightState, OrderStopType, OrderStopTypeMap } from "@/constants/order";
import { isSameEntity } from "@/utils/sameEntity";
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";
import { RouteStatus, RouteStopStatus } from "@/constants/trip";
import i18n from "@/plugins/i18n";
import { TravelOrderPaymentTypeLabel } from "@/constants/travelOrder";
import { useBaseView, useFreights } from "@/composables";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import type { Order, OrderFreightDto } from "@/api/orders/dto/order.dto";

const t = i18n.global.t;

export function useDisableRules() {
  const { hasRouteFreightMismatch } = useFreights();

  function createTripDispatchDisableRules(
    routeOrRoutes: MaybeRefOrGetter<TripRoute[] | TripRoute>,
    dispatchRequired = false,
    orderFreights: MaybeRefOrGetter<OrderFreightDto[]> = [],
  ): DisabledRule[] {
    const raw = toValue(routeOrRoutes);

    const routes: TripRoute[] = Array.isArray(raw) ? raw : [raw];

    if (!routes.length) {
      return [];
    }

    function getRoutePosition(route: TripRoute | undefined): string {
      return route ? (routes.findIndex((item) => isSameEntity(item, route)) + 1).toString() : "";
    }

    const travelOrders = routes.flatMap((route) => route.travelOrders);
    const travelOrdersWithoutPayment = travelOrders
      .map((to) => {
        const paymentTypesWithoutAmount = to.payments
          .filter((payment) => !payment.amount && payment.amount !== 0)
          .map(({ type }) => type)
          .filter(Boolean);

        if (!paymentTypesWithoutAmount.length) {
          return null;
        }

        return {
          ...to,
          paymentTypesWithoutAmount,
        };
      })
      .filter(Boolean);

    const activeRoutes = routes.filter((route) => route.status !== RouteStatus.cancelled);
    const routeStops = routes.flatMap((route) => route.routeStops);
    const activeRouteStops = activeRoutes.flatMap((route) => route.routeStops);
    const activeRouteFreights = activeRoutes.flatMap((route) => route.freights);
    const activeRouteFreightsMap = new Map(activeRouteFreights.map((freight) => [freight.id, freight]));

    const createdRouteStopsWithoutFreight = activeRouteStops.filter((stop) => {
      if (!stop.use || stop.isDeleted) {
        return false;
      }

      const activeFreights = stop.freights.filter((freightId) => {
        const freight = activeRouteFreightsMap.get(freightId);
        return freight && !freight?.isDeleted;
      });

      return !activeFreights.length;
    });

    const routesWithoutMandatoryStops = routes.filter((route) => {
      const createdDeliveries = route.routeStops.filter(
        (stop) => stop.type === OrderStopType.delivery && stop.use && !stop.isDeleted,
      );
      const createdPickUps = route.routeStops.filter(
        (stop) => stop.type === OrderStopType.pickup && stop.use && !stop.isDeleted,
      );

      return !createdDeliveries.length || !createdPickUps.length;
    });

    const deletedFreightIds = new Set(
      routes
        .flatMap((route) => route.freights)
        .filter((freight) => freight.isDeleted)
        .map((freight) => freight.id),
    );

    const pickUpFreights = routeStops
      .filter((stop) => stop.type === OrderStopType.pickup && !stop.isDeleted)
      .flatMap((stop) => stop.freights)
      .filter((id) => !deletedFreightIds.has(id));

    const deliveriesFreights = routeStops
      .filter((stop) => stop.type === OrderStopType.delivery && !stop.isDeleted)
      .flatMap((stop) => stop.freights)
      .filter((id) => !deletedFreightIds.has(id));

    const deliveryFreightsSet = new Set(deliveriesFreights);
    const freightsMismatch = pickUpFreights.some((f) => !deliveryFreightsSet.has(f));
    const unUpdatedRouteFreights = activeRouteFreights.filter(
      (routeFreight) => routeFreight.state !== OrderFreightState.updated,
    );

    const routeFreightMismatch = hasRouteFreightMismatch(unUpdatedRouteFreights, toValue(orderFreights));

    const routesNeededDispatch = dispatchRequired ? routes.filter((route) => !route.isDispatched) : undefined;

    return [
      {
        disabled: Boolean(travelOrdersWithoutPayment.length),
        reason: travelOrdersWithoutPayment
          ?.map((item) => {
            return item?.paymentTypesWithoutAmount.map((paymentType) => {
              return `${t("trip.add-rate-for-to", { rateType: TravelOrderPaymentTypeLabel[paymentType!] })} ${item?.name}`;
            });
          })
          .join("\n"),
      },
      {
        disabled: Boolean(routesWithoutMandatoryStops.length),
        reason: routesWithoutMandatoryStops
          .map((route) => `${t("trip.add-at-least-one-pickup-delivery-route")} #${getRoutePosition(route)}`)
          .join("\n"),
      },
      {
        disabled: Boolean(createdRouteStopsWithoutFreight.length),
        reason: [
          t("trip.add-route-freight"),
          ...createdRouteStopsWithoutFreight.map(
            (stop) => `${OrderStopTypeMap[stop.type]} #${stop.number} ${t("general.stop").toLowerCase()}`,
          ),
        ].join("\n"),
      },
      {
        disabled: freightsMismatch,
        reason: t("trip.each-freight-pick-up-delivery"),
      },
      {
        disabled: routeFreightMismatch,
        reason: t("trip.update-route-freight-details"),
      },
      {
        disabled: Boolean(routesNeededDispatch?.length),
        reason:
          routesNeededDispatch
            ?.map((route) => `${t("general.route")} #${getRoutePosition(route)} ${t("trip.not-dispatched")}`)
            .join("\n") || "",
      },
    ];
  }

  function createAddCheckCallDisableRules(
    confirmDispatch: boolean,
    nextStop: MaybeRefOrGetter<RouteStopDto | undefined>,
    routeStatus: RouteStatus,
  ) {
    const nextStopRaw = toValue(nextStop);
    const { hasAccess } = useBaseView();

    return [
      {
        disabled: !hasAccess(DispatcherPermissionValue.CheckCallsEdit),
        reason: t("permission.create-check-call"),
      },
      {
        disabled: !confirmDispatch,
        reason: t("trip.check-call-not-confirmed"),
      },
      {
        disabled: () => {
          if (!nextStopRaw) {
            return false;
          }

          const nextStopDisabledStatuses = [RouteStopStatus.waitingForLoading, RouteStopStatus.waitingForGoodToGo];

          return nextStopDisabledStatuses.includes(nextStopRaw.status);
        },
        reason: t("trip.check-call-driver-checked-in"),
      },
      {
        disabled: routeStatus !== RouteStatus.inProgress,
        reason: t("trip.check-call-route-not-in-progress"),
      },
    ];
  }

  function createAddTruckToRouteDisableRules(route: MaybeRefOrGetter<TripRoute>): DisabledRule[] {
    const routeRaw = toValue(route);
    const hasAtLeastOneActivePickUp = routeRaw.routeStops.some(
      (routeStop) => routeStop.use && routeStop.type === OrderStopType.pickup,
    );
    return [
      {
        disabled: !hasAtLeastOneActivePickUp,
        reason: t("trip.add-at-least-one-pickup"),
      },
    ];
  }

  function createSaveOrderDisableRules(order: MaybeRefOrGetter<Order>): DisabledRule[] {
    const orderRaw = toValue(order);

    if (!orderRaw?.orderStops || !orderRaw.freights) {
      return [{ disabled: false, reason: "" }];
    }

    const freights = orderRaw.freights;
    const freightsById = new Map(freights.map((freight) => [freight.id, freight]));

    const missingFreights = freights
      .filter((freight) => !freight.isDeleted && !freight.$usedIn?.length)
      .map((freight) => `Freight${freight.$number}`);

    const stopsWithAllDeletedFreights = orderRaw.orderStops
      .filter((stop) => {
        const stopFreights = stop.freights ?? [];

        if (!stopFreights.length || stop.isDeleted) {
          return false;
        }

        return stopFreights.every((id) => freightsById.get(id)?.isDeleted);
      })
      .map((stop) => `${OrderStopTypeMap[stop.type]} #${stop.$number}`);

    return [
      {
        disabled: missingFreights.length > 0,
        reason: t("orders.add-freights-to-delivery-stop", { freights: missingFreights.join(", ") }),
      },
      {
        disabled: stopsWithAllDeletedFreights.length > 0,
        reason: t("orders.add-freight-to-stop", { stops: stopsWithAllDeletedFreights.join(", ") }),
      },
    ];
  }

  return {
    createTripDispatchDisableRules,
    createAddCheckCallDisableRules,
    createAddTruckToRouteDisableRules,
    createSaveOrderDisableRules,
  };
}
